/**
 * Helpers for showing safe, user-friendly error messages.
 *
 * Errors that bubble up from the backend/API often carry technical detail in
 * their `message` (endpoint paths, upstream service names, URLs, status codes,
 * machine-readable codes). We never want that surfaced to a user. These helpers
 * let a known-friendly message through, but fall back to plain copy for anything
 * that looks like internal/technical detail.
 */

// Substrings that mark a message as internal/technical and unsafe to display.
const TECHNICAL_PATTERNS = [
  /https?:\/\//i, // URLs
  /:\/\//, // protocol-relative URLs
  /\b(?:endpoint|upstream|gateway|proxy)\b/i,
  /\b(?:dsp|dotstudio|cleeng|sendy|spotlight|cms|hls|directus)\b/i,
  /\/v\d+\//, // versioned API paths e.g. /v2/
  /\b(?:fetch|json|networkerror|typeerror|econn|etimedout|enotfound)\b/i,
  /\bstatus\s*\d{3}\b/i, // "status 500"
  /\(\s*\d{3}\s*\)/, // "(500)"
  /\b\d{3}\s+(?:bad gateway|internal server error|not found|unauthorized|forbidden)\b/i,
  /\bat\b.*\.js/i, // stack-trace-ish
  /^[a-z0-9]+(?:_[a-z0-9]+)+$/, // snake_case machine codes e.g. session_expired
];

/**
 * Decide whether an error message is safe to show to a user as-is.
 *
 * @param {string} message
 * @returns {boolean}
 */
export const isUserSafeMessage = message => {
  if (typeof message !== 'string') {
    return false;
  }

  const trimmed = message.trim();
  if (!trimmed || trimmed.length > 120) {
    return false;
  }

  return !TECHNICAL_PATTERNS.some(pattern => pattern.test(trimmed));
};

/**
 * Return a user-friendly message for an error.
 *
 * If the error's message reads like something a user can understand, it is
 * returned untouched. Otherwise the provided fallback is used and the original
 * error is logged for debugging.
 *
 * @param {unknown} error - The caught error (or a string message).
 * @param {string} fallback - Plain copy to show when the error isn't user-safe.
 * @returns {string}
 */
export const safeUserMessage = (error, fallback) => {
  const message = typeof error === 'string' ? error : error?.message;

  if (isUserSafeMessage(message)) {
    return message.trim();
  }

  if (error) {
    console.error('[error]', error);
  }

  return fallback;
};

/**
 * Copy for a failed `/v1/auth/*` call. The server's own message is shown only where it is written
 * for the viewer (wrong credentials, an already-registered email, the password rule, a dead reset
 * link); every vendor or transport failure gets the page's own fallback.
 *
 * @param {unknown} error - usually an `ApiError` from src/api
 * @param {string} fallback
 * @returns {string}
 */
export const authErrorMessage = (error, fallback) => {
  switch (error?.code) {
    case 'LOGIN_REQUIRED':
    case 'BAD_REQUEST':
      return safeUserMessage(error, fallback);
    case 'RATE_LIMITED':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      if (error) {
        console.error('[auth]', error);
      }
      return fallback;
  }
};
